<template>
  <span class="kb-icon" :class="rootClass" v-bind="restAttrs">
    <component v-if="LucideComp" :is="LucideComp" :size="numericSize" />
    <span v-else v-html="html"></span>
    <span v-if="title" class="sr-only">{{ title }}</span>
  </span>
</template>

<script setup>
  import { ref, computed, useAttrs } from 'vue'
  import {
    getIconHTML,
    parseIconString,
    ICON_TYPES
  } from '@/components/plugins/KnowledgeBase/services/IconRegistry.js'

  const props = defineProps({
    icon: { type: [String, Object], default: '' },
    size: { type: [Number, String], default: 16 },
    title: { type: String, default: '' },
    klass: { type: [String, Array, Object], default: '' }
  })

  const attrs = useAttrs()

  const numericSize = computed(() => {
    const n = Number(props.size)
    return Number.isFinite(n) ? n : 16
  })

  // Try to load lucide components lazily
  const lucide = ref(null)
  try {
    import('lucide-vue-next')
      .then((mod) => {
        lucide.value = mod
      })
      .catch(() => {})
  } catch {}

  const info = computed(() => {
    try {
      return parseIconString(String(props.icon || ''))
    } catch {
      return null
    }
  })

  const isLucide = computed(() => info.value?.type === ICON_TYPES.LUCIDE)

  const LucideComp = computed(() => {
    try {
      if (!lucide.value || !isLucide.value) return null
      const base = String(info.value?.value || '')
      if (!base) return null
      const pascal = base
        .split('-')
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join('')
      return lucide.value[pascal] || lucide.value[`${pascal}Icon`] || null
    } catch {
      return null
    }
  })

  function toClassString(input) {
    if (!input) return ''
    if (typeof input === 'string') return input
    if (Array.isArray(input)) return input.filter(Boolean).join(' ')
    if (typeof input === 'object')
      return Object.keys(input)
        .filter((k) => !!input[k])
        .join(' ')
    return ''
  }

  const rootClass = computed(() => {
    const fromProp = toClassString(props.klass)
    const fromAttr = toClassString(attrs.class)
    return [fromProp, fromAttr].filter(Boolean).join(' ')
  })

  const innerClass = computed(() => rootClass.value)

  const html = computed(() => {
    try {
      return (
        getIconHTML(String(props.icon || ''), {
          size: `${numericSize.value}px`,
          className: innerClass.value
        }) || ''
      )
    } catch {
      return ''
    }
  })

  // Pass-through attrs except class (we already merged it)
  const restAttrs = computed(() => {
    const { class: _cls, ...rest } = attrs
    return rest
  })
</script>

<style scoped>
  .kb-icon {
    display: inline-flex;
    vertical-align: middle;
    line-height: 0;
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
